import React, { useCallback, useEffect, useState } from "react";
import ComponentDetailModal from '../../components/ComponentDetailModal';
import { componenteService } from "../../services/componenteService";
import { getPlantillaNormalized, ensureArray } from "../../utils/plantillas";
import { formatNumber } from "../../utils/numberFormat";

export default function ComponenteConItemsForm({ tipo, titulo }) {
    const [nombreComponente, setNombreComponente] = useState("");
    const [nuevoItem, setNuevoItem] = useState({ nombre: "", cantidad: "", costo: "" });
    const [items, setItems] = useState([]);
    const [componentesCreados, setComponentesCreados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [plantillaActiva, setPlantillaActiva] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editingComponent, setEditingComponent] = useState(null);

    const cargarComponentesCreados = useCallback(async () => {
        try {
            const data = await componenteService[tipo]?.getAll();
            // Filtrar solo componentes únicos por nombre
            const unicos = data
                .map(c => ({ ...c, items: ensureArray(c.items) }))
                .filter((item, idx, self) =>
                    self.findIndex((t) => t.nombre === item.nombre) === idx
                );
            setComponentesCreados(unicos);
        } catch (error) {
            console.error("Error cargando componentes:", error);
        }
    }, [tipo]);

    useEffect(() => {
        if (Object.keys(componenteService).includes(tipo)) {
            cargarComponentesCreados();
        } else {
            console.error(`Tipo inválido: ${tipo}`);
        }
    }, [tipo, cargarComponentesCreados]);

    const cargarPlantilla = () => {
        const plantilla = getPlantillaNormalized(tipo);
        if (plantilla) {
            setPlantillaActiva(plantilla);
            // Cargar items de la plantilla (ya normalizados)
            const itemsPlantilla = (plantilla.items || []).map(item => ({
                nombre: item.nombre,
                cantidad: item.cantidad || 1,
                costo: item.costo || 0
            }));
            setItems(itemsPlantilla);
        }
    };

    const agregarItem = () => {
        if (!nuevoItem.nombre || !nuevoItem.costo) {
            alert("Completar nombre y costo del ítem");
            return;
        }
        setItems((prev) => [...prev, { 
            ...nuevoItem, 
            cantidad: Number(nuevoItem.cantidad) || 1,
            costo: Number(nuevoItem.costo) 
        }]);
        setNuevoItem({ nombre: "", cantidad: "", costo: "" });
    };

    const editarCantidadItem = (index, nuevaCantidad) => {
        setItems((prev) => {
            const itemsActualizados = [...prev];
            itemsActualizados[index] = {
                ...itemsActualizados[index],
                cantidad: Number(nuevaCantidad) || 1
            };
            return itemsActualizados;
        });
    };

    const editarPrecioItem = (index, nuevoPrecio) => {
        setItems((prev) => {
            const itemsActualizados = [...prev];
            itemsActualizados[index] = {
                ...itemsActualizados[index],
                costo: Number(nuevoPrecio) || 0
            };
            return itemsActualizados;
        });
    };

    const eliminarItem = (index) => {
        setItems((prev) => prev.filter((_, i) => i !== index));
    };

    const guardarComponente = async () => {
        if (!nombreComponente.trim()) {
            alert("Debes darle un nombre al componente");
            return;
        }

        if (items.length === 0) {
            alert("Debe agregar al menos un item");
            return;
        }

        if (!Object.keys(componenteService).includes(tipo)) {
            alert("Error: Tipo inválido");
            return;
        }

        setLoading(true);
        try {
            // Mapear tipo a nombre de entidad correcto (debe coincidir con @JsonSubTypes en backend)
            const tipoMap = {
                gabinete: "Gabinete",
                puerta: "Puerta",
                accesorio: "Accesorio",
                materialesFrio: "MaterialesFrio",  // ← PLURAL como espera el backend
                materialesElectricos: "MaterialesElectricos",  // ← PLURAL como espera el backend
                unidadCondensadora: "UnidadCondensadora",
                evaporadorCubico: "Evaporador Cúbico",
                panel: "Panel"
            };

            const payload = {
                ...(editingComponent || {}),
                nombre: nombreComponente, 
                items,
                type: tipoMap[tipo] || tipo
            };
            if (editingId) {
                await componenteService[tipo].update(editingId, payload);
                alert("Componente actualizado");
            } else {
                await componenteService[tipo].create(payload);
                alert("Guardado con éxito");
            }
            cancelarEdicion();
            await cargarComponentesCreados();
        } catch (err) {
            console.error(err);
            alert("Error al guardar");
        } finally {
            setLoading(false);
        }
    };

    const eliminarComponente = async (id, nombre) => {
        if (window.confirm(`¿Eliminar "${nombre}"?`)) {
            try {
                await componenteService[tipo].delete(id);
                alert("Componente eliminado");
                cargarComponentesCreados();
            } catch (error) {
                console.error("Error al eliminar:", error);
                alert("Error al eliminar componente");
            }
        }
    };

    const editarComponente = (componente) => {
        setEditingId(componente.id);
        setEditingComponent(componente);
        setNombreComponente(componente.nombre || "");
        setItems(ensureArray(componente.items).map((item) => ({
            ...item,
            cantidad: item.cantidad ?? 1,
            costo: item.costo ?? 0,
        })));
        setPlantillaActiva(null);
    };

    const cancelarEdicion = () => {
        setEditingId(null);
        setEditingComponent(null);
        setNombreComponente("");
        setItems([]);
        setNuevoItem({ nombre: "", cantidad: "", costo: "" });
        setPlantillaActiva(null);
    };

    const total = items.reduce((acc, it) => acc + (Number(it.costo) * (Number(it.cantidad) || 1)), 0);

    const [modalComp, setModalComp] = useState(null);
    const verDetalle = (comp) => {
        // normalize items to avoid transient non-array values
        const normalized = { ...comp, items: ensureArray(comp.items) };
        setModalComp({ ...normalized, tipo });
    };
    const cerrarModal = () => setModalComp(null);

    return (
        <div className="components-wrapper">
            <div className="components-container">
                <h2>{titulo}</h2>

                {/* SELECTOR DE PLANTILLA */}
                <div className="card" style={{ backgroundColor: "#f0f4ff", borderLeft: "4px solid #3b82f6" }}>
                    <h3>📋 Plantilla Predefinida</h3>
                    <button 
                        className="btn-save" 
                        onClick={cargarPlantilla}
                        style={{ backgroundColor: plantillaActiva ? "#22c55e" : "#3b82f6" }}
                    >
                        {plantillaActiva ? "✓ Plantilla Cargada" : "Cargar Plantilla Predefinida"}
                    </button>
                    {plantillaActiva && (
                        <p style={{ color: "#22c55e", marginTop: 8, fontWeight: "bold" }}>
                            Items de plantilla cargados - Puedes editar precios o agregar items extra
                        </p>
                    )}
                </div>

                {/* NOMBRE DEL COMPONENTE */}
                <div className="card">
                    <label style={{ fontWeight: "bold" }}>Nombre del componente creado:</label>
                    <input
                        type="text"
                        placeholder="Ej: Puerta_Trabajo_001_Nov2025"
                        value={nombreComponente}
                        onChange={(e) => setNombreComponente(e.target.value)}
                        style={{ marginTop: 8, width: "100%" }}
                    />
                </div>

                {/* AGREGAR ITEMS */}
                <div className="card">
                    <h3>Agregar/Editar Items</h3>
                    <div className="item-row">
                        <input
                            type="text"
                            placeholder="Nombre del item"
                            name="nombre"
                            value={nuevoItem.nombre}
                            onChange={(e) =>
                                setNuevoItem((prev) => ({ ...prev, nombre: e.target.value }))
                            }
                        />
                        <input
                            type="number"
                            placeholder="Cantidad"
                            name="cantidad"
                            value={nuevoItem.cantidad}
                            onChange={(e) =>
                                setNuevoItem((prev) => ({ ...prev, cantidad: e.target.value }))
                            }
                        />
                        <input
                            type="number"
                            placeholder="Costo"
                            name="costo"
                            value={nuevoItem.costo}
                            onChange={(e) =>
                                setNuevoItem((prev) => ({ ...prev, costo: e.target.value }))
                            }
                        />
                        <button className="btn-save" type="button" onClick={agregarItem}>
                            +
                        </button>
                    </div>

                    {items.length > 0 && (
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Cantidad</th>
                                    <th>Costo</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it, idx) => (
                                    <tr key={idx}>
                                        <td>{it.nombre}</td>
                                        <td>
                                            <input
                                                type="number"
                                                step="1"
                                                value={it.cantidad || 1}
                                                onChange={(e) => editarCantidadItem(idx, e.target.value)}
                                                style={{
                                                    width: "100%",
                                                    padding: "4px",
                                                    border: "1px solid #ddd",
                                                    borderRadius: "4px"
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={it.costo}
                                                onChange={(e) => editarPrecioItem(idx, e.target.value)}
                                                style={{
                                                    width: "100%",
                                                    padding: "4px",
                                                    border: "1px solid #ddd",
                                                    borderRadius: "4px"
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                onClick={() => eliminarItem(idx)}
                                                className="text-red-600 font-bold"
                                            >
                                                ✕
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                                    <p style={{ marginTop: 10, fontWeight: "bold" }}>
                                        ✅ Total: ${formatNumber(total)} ARS
                                    </p>

                    <button
                        className="btn-save"
                        type="button"
                        onClick={guardarComponente}
                        disabled={loading || !nombreComponente.trim()}
                    >
                        {loading
                            ? "Guardando..."
                            : editingId ? `Guardar cambios de ${titulo}` : `Guardar ${titulo}`}
                    </button>
                    {editingId && (
                        <button
                            className="btn-cancel"
                            type="button"
                            onClick={cancelarEdicion}
                            disabled={loading}
                        >
                            Cancelar edición
                        </button>
                    )}
                </div>

                {/* COMPONENTES CREADOS */}
                <h3 style={{ marginTop: 30 }}>📦 Componentes Creados ({componentesCreados.length})</h3>
                {componentesCreados.length === 0 ? (
                    <p style={{ color: "#666" }}>Aún no hay componentes creados</p>
                ) : (
                    <div className="grid-items">
                        {componentesCreados.map((comp) => (
                            <div key={comp.id} className="card small" style={{ position: "relative", cursor: 'pointer' }} onClick={() => verDetalle(comp)}>
                                <p style={{ fontWeight: "bold", marginBottom: 4 }}>{comp.nombre}</p>
                                <p style={{ fontSize: 12, color: "#666" }}>{comp.items?.length || 0} ítems</p>
                                <p style={{ fontWeight: "bold", color: "#22c55e" }}>
                                    ${formatNumber(comp.costoTotal || 0)}
                                </p>
                                <button
                                    type="button"
                                    className="btn-edit"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        editarComponente(comp);
                                    }}
                                >
                                    ✏️ Editar
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); eliminarComponente(comp.id, comp.nombre); }}
                                    style={{
                                        position: "absolute",
                                        top: 8,
                                        right: 8,
                                        background: "#ef4444",
                                        color: "white",
                                        border: "none",
                                        borderRadius: 4,
                                        padding: "4px 8px",
                                        cursor: "pointer",
                                        fontSize: 12
                                    }}
                                >
                                    🗑️ Eliminar
                                </button>
                            </div>
                        ))}
                        {modalComp && (
                            <ComponentDetailModal comp={modalComp} onClose={cerrarModal} />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
